/**
 * Which of §9.4's three return states the back URL names.
 *
 * Two drivers bring the customer back to `/fizetes/{token}`, and they do not
 * come back the same way:
 *
 * - the **mock** payment page is ours, and it appends
 *   `?result=success|fail|cancel|timeout` (`routes/MockPaymentPage.tsx`);
 * - **SimplePay** appends `?r=<base64 JSON>&s=<signature>` — v2.1 §3.12 — and
 *   the decoded JSON's `e` is `SUCCESS` · `FAIL` · `CANCEL` · `TIMEOUT`.
 *
 * Until stage `074_sandbox_cases` (7 October 2026, `build/FIXES.md` item 68)
 * nothing read the second form. `ReturnPage` read `result` alone and defaulted
 * a missing one to `success`, so a customer coming back from a SimplePay
 * payment that failed, was cancelled or timed out reached the page with no
 * `result` at all and was shown *Fizetés feldolgozás alatt.* for sixty seconds
 * and then *A visszaigazolást e-mailben küldjük…* — §9.4's **third** state,
 * *A fizetés nem sikerült.* with the retry and the kept basket, was
 * unreachable under `PAYMENT_DRIVER=simplepay`. It was not reachable in
 * practice before that switch-on, which is why no earlier stage met it.
 *
 * **The signature `s` is not verified here, and cannot be.** The merchant
 * secret key is a server credential (`SIMPLEPAY_SECRET_KEY`) and never reaches
 * a browser; `readBackUrl` in `supabase/functions/_shared/payments/simplepay.ts`
 * is where §3.12's check belongs and it already exists. Not verifying it here
 * is sound for one reason and only one: **this page writes nothing.** §9.4
 * step 3 makes the back URL display only, the `paid` state is read off the
 * order's own status rather than off this parameter, and the IPN of step 4 is
 * the only thing that moves an order. The same was already true of `result`,
 * which carries no signature either. A forged parameter can make the page say
 * the payment did not go through; it cannot make the order untrue.
 */

/** The four results §9.4's return table distinguishes. `success` is one row,
 *  the other three are its third row — *failed, cancelled or timeout*. */
export type BackUrlResultName = 'success' | 'fail' | 'cancel' | 'timeout'

const SIMPLEPAY_EVENTS: Record<string, BackUrlResultName> = {
  SUCCESS: 'success',
  FAIL: 'fail',
  CANCEL: 'cancel',
  TIMEOUT: 'timeout',
}

/**
 * Base64 as it may arrive on a query string, put back into the shape `atob`
 * takes.
 *
 * **The encoding question `074_sandbox_cases`'s spec audit raised, answered
 * from the documentation and then made not to matter.** Standard base64 uses
 * `+` and `/`, and `URLSearchParams.get` applies
 * `application/x-www-form-urlencoded` rules — in which a literal `+` is a
 * **space**. A raw `+` in `r` would therefore reach `atob` as a space, `atob`
 * would throw, and the whole fix below would fall back to the `success`
 * default: exactly the defect it exists to close.
 *
 * **SimplePay percent-encodes both parameters**, so this does not arise. §3.12
 * of the v2.1 documentation prints the worked example in full, and it ends
 * `…fQ%3D%3D&s=El%2Fnvex9…` — `=` as `%3D` and `/` as `%2F`, which is
 * percent-encoding of the base64 alphabet and nothing else. (It has to be:
 * §4.7's own PHP sample reads `$_REQUEST['r']`, and PHP decodes `+` to a space
 * too, so an unencoded `+` would break SimplePay's own SDK.)
 *
 * It is handled anyway. Nobody on this project has yet seen a real back URL —
 * B10 means no transaction can be started — so the sentence above is a reading
 * of a document, and three lines that make the reading not matter are cheaper
 * than being right about it. A space is put back to `+`, and base64url's `-`
 * and `_` are accepted, in case the encoding is ever not what §3.12 prints.
 */
function base64FromQuery(raw: string): string {
  return raw.replaceAll(' ', '+').replaceAll('-', '+').replaceAll('_', '/')
}

/**
 * `r` as SimplePay sends it, decoded far enough to read `e` — and no further.
 * Anything that is not base64 of JSON with a known `e` answers null, so the
 * caller falls back rather than guessing.
 */
export function simplePayBackEvent(r: string | null): BackUrlResultName | null {
  if (r === null || r.trim() === '') return null
  let decoded: unknown
  try {
    decoded = JSON.parse(atob(base64FromQuery(r)))
  } catch {
    return null
  }
  if (decoded === null || typeof decoded !== 'object') return null
  const event = String((decoded as Record<string, unknown>)['e'] ?? '').toUpperCase()
  return SIMPLEPAY_EVENTS[event] ?? null
}

/**
 * The one place that decides which state the page is in, for either driver.
 *
 * SimplePay's `r` wins when it is there and can be read, because a page that
 * carries it was reached from SimplePay and nothing else put it there. Then
 * `result`, which is the mock page's. A back URL with neither is the state
 * §9.4's table calls *Success · not yet* — which is what the page has always
 * defaulted to, and which the order's own status then settles.
 *
 * **What `result` means is unchanged.** It is returned as it arrived, so
 * anything that is not `success` still reads as the failure row of §9.4's
 * table, exactly as `search.get('result') ?? 'success'` did in `ReturnPage`
 * before this function existed. Narrowing it to the three names the mock page
 * can produce would have been a relaxation nobody asked for.
 */
export function backUrlResult(search: URLSearchParams): string {
  const fromSimplePay = simplePayBackEvent(search.get('r'))
  if (fromSimplePay !== null) return fromSimplePay
  return search.get('result') ?? 'success'
}
